import { STORAGE_LANGUAGE_NAME } from "constants/variables";
import { useSelector } from "react-redux";

// Selectors
import * as userSelectors from "redux/user/selectors";

// Types
import { CurrencySignPositionEnum } from "types/Currency";

export const currencySymbols = {
  USD: { iso: "USD", symbol: "$", position: CurrencySignPositionEnum.AFTER },
  INR: { iso: "INR", symbol: "₹", position: CurrencySignPositionEnum.BEFORE },
  BDT: { iso: "BDT", symbol: "৳", position: CurrencySignPositionEnum.BEFORE },
  AZN: { iso: "AZN", symbol: "₼", position: CurrencySignPositionEnum.AFTER },
  BRL: { iso: "BRL", symbol: "R$", position: CurrencySignPositionEnum.BEFORE },
  EUR: { iso: "EUR", symbol: "€", position: CurrencySignPositionEnum.BEFORE },
  KZT: { iso: "KZT", symbol: "₸", position: CurrencySignPositionEnum.AFTER },
  PKR: { iso: "PKR", symbol: "Rs", position: CurrencySignPositionEnum.AFTER },
  TRY: { iso: "TRY", symbol: "₺", position: CurrencySignPositionEnum.BEFORE },
  UZS: { iso: "UZS", symbol: "UZS", position: CurrencySignPositionEnum.AFTER },
  MMK: { iso: "MMK", symbol: "MMK", position: CurrencySignPositionEnum.AFTER },
  KES: { iso: "KES", symbol: "KES", position: CurrencySignPositionEnum.AFTER },
  LKR: { iso: "LKR", symbol: "LKR", position: CurrencySignPositionEnum.AFTER },
  GBP: { iso: "GBP", symbol: "£", position: CurrencySignPositionEnum.BEFORE },
  VND: { iso: "VND", symbol: "₫", position: CurrencySignPositionEnum.AFTER },
  KRW: { iso: "KRW", symbol: "₩", position: CurrencySignPositionEnum.AFTER },
};

const separators = {
  EN: {
    millionSeparator: " ",
    thousandSeparator: " ",
    fractionalSeparator: ",",
  },
  Hi: {
    millionSeparator: " ",
    thousandSeparator: " ",
    fractionalSeparator: ",",
  },
  RU: {
    millionSeparator: " ",
    thousandSeparator: " ",
    fractionalSeparator: ",",
  },
  BN: {
    millionSeparator: " ",
    thousandSeparator: ",",
    fractionalSeparator: ".",
  },
  TR: {
    millionSeparator: " ",
    thousandSeparator: " ",
    fractionalSeparator: ",",
  },
  UZ: {
    millionSeparator: " ",
    thousandSeparator: " ",
    fractionalSeparator: ".",
  },
  KZ: {
    millionSeparator: " ",
    thousandSeparator: " ",
    fractionalSeparator: ",",
  },
  MM: {
    millionSeparator: " ",
    thousandSeparator: ",",
    fractionalSeparator: ".",
  },
  SI: {
    millionSeparator: " ",
    thousandSeparator: " ",
    fractionalSeparator: ".",
  },
  AZ: {
    millionSeparator: " ",
    thousandSeparator: " ",
    fractionalSeparator: ",",
  },
  VI: {
    millionSeparator: " ",
    thousandSeparator: " ",
    fractionalSeparator: ",",
  },
  NE: {
    millionSeparator: " ",
    thousandSeparator: " ",
    fractionalSeparator: ",",
  },
  FR: {
    millionSeparator: " ",
    thousandSeparator: " ",
    fractionalSeparator: ",",
  },
  KO: {
    millionSeparator: " ",
    thousandSeparator: " ",
    fractionalSeparator: ",",
  },
};

export const getSeparatorsForLanguage = (language) => {
  return (
    separators[language] || { thousandSeparator: ",", fractionalSeparator: "." }
  );
};

export function splitAmountString(amountString, fractionalSeparatorString) {
  const parts = amountString.split(fractionalSeparatorString);
  if (parts.length > 1) {
    const fractionalPart = parts[1];
    if (fractionalPart === "00") {
      return amountString.slice(0, -3);
    }
  }
  return amountString;
}

export const formatFractionalAndThousand = (numberAmount, languageString) => {
  const { thousandSeparator, fractionalSeparator, millionSeparator } =
    getSeparatorsForLanguage(languageString);

  const numberString = numberAmount.toString();

  const { length } = numberString;

  if (length === 3) {
    // только копейки '0,30
    const fractionalPart = numberString.slice(-2);
    const integerPart = numberString.slice(0, 1);
    return `${integerPart}${fractionalSeparator}${fractionalPart}`;
  }

  if (length >= 4 && length <= 5) {
    // от '10 00' до '100 00'
    const fractionalPart = numberString.slice(-2);
    const mainPart = numberString.slice(-5, -2);

    return `${mainPart}${fractionalSeparator}${fractionalPart}`;
  }

  if (length >= 6 && length <= 8) {
    // от '1 000 00' до '100 000 00'
    const fractionalPart = numberString.slice(-2);
    const mainPart = numberString.slice(-5, -2);
    const thousandPart = numberString.slice(-8, -5);

    return `${thousandPart}${thousandSeparator}${mainPart}${fractionalSeparator}${fractionalPart}`;
  }

  if (length === 9) {
    // '1 000 000 00'
    const fractionalPart = numberString.slice(-2);
    const mainPart = numberString.slice(-5, -2);
    const thousandPart = numberString.slice(-8, -5);
    const millionPart = numberString.slice(0, 1);

    return `${millionPart}${millionSeparator}${thousandPart}${thousandSeparator}${mainPart}${fractionalSeparator}${fractionalPart}`;
  }

  if (length === 10) {
    // '10 000 000 00'
    const fractionalPart = numberString.slice(-2);
    const mainPart = numberString.slice(-5, -2);
    const thousandPart = numberString.slice(-8, -5);
    const millionPart = numberString.slice(0, 2);

    return `${millionPart}${millionSeparator}${thousandPart}${thousandSeparator}${mainPart}${fractionalSeparator}${fractionalPart}`;
  }

  return "0";
};

const useFormatBalance = () => {
  const currency = useSelector(userSelectors.currency);
  const userLanguage = localStorage.getItem(STORAGE_LANGUAGE_NAME) || "EN";

  return (number) => {
    if (!number && number !== 0) {
      return "0";
    }

    const formattedWithCorrectSeparatorsRow = formatFractionalAndThousand(
      number,
      userLanguage,
    );

    // Отрезаем копейки, если они === "00"

    const formattedWithCorrectSeparators = splitAmountString(
      formattedWithCorrectSeparatorsRow,
      getSeparatorsForLanguage(userLanguage).fractionalSeparator,
    );

    // Подставляем знак валюты до или после суммы в зависимости от языка
    if (currency) {
      if (currency.signPosition === CurrencySignPositionEnum.AFTER) {
        return `${formattedWithCorrectSeparators} ${currency?.sign}`;
      }
      if (currency.signPosition === CurrencySignPositionEnum.BEFORE) {
        return `${currency?.sign} ${formattedWithCorrectSeparators}`;
      }
    }

    return formattedWithCorrectSeparators;
  };
};

export default useFormatBalance;
